import type { OtpDetails } from 'ehs-react';
import { createStore, showError } from 'ehs-react';
import {
  doAuthenticateUserCall,
  sendTwoFactorPassword,
  verifyTwoFactorPassword
} from './ajax';
import { PortletStoreApi } from '../App';
import type { EHealthLoginUtil } from '../util/integrationWithOldJs';
import {
  getHpdSelect,
  getOtpPasswordInput,
  saveHpdSelection
} from '../util/integrationWithOldJs';
import {
  getAllAvailableOtpDetails,
  getUniqueOtpDetails
} from '../util/login-response-helper';

import type { at } from '../generated/Beans';

declare const eHealthLoginUtil: EHealthLoginUtil;

export type VIEW_STATE =
  | 'INITIAL'
  | 'TWO_FACTOR_METHOD_SELECTION'
  | 'TWO_FACTOR_ENTERING'
  | 'HPD_SELECTION';

export type LoginCredentialsStore = {
  viewState: VIEW_STATE;
  pageLoading: boolean;
  authenticated: boolean;
  userName: string;
  password: string;
  fingerprint?: string;
  assertionId?: string;
  otpSendDetails?: OtpDetails;

  qrCode?: string;
  appSecretKey?: string;
  otpAuthUri?: string;
  applyOtpValue: (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void
  ) => void;

  availableTwoFactorMethods: OtpDetails[];
  selectedTwoFactorMethod?: OtpDetails;
  setSelectedTwoFactorMethod: (selectedEntry: OtpDetails) => void;
  applySelectedTwoFactorMethod: (selectedEntry: OtpDetails) => void;

  availableHpdCompanies: at.sense.hooks.login.hpd.HPDParentComboBean[];
  selectedHpdCompany?: at.sense.hooks.login.hpd.HPDParentComboBean;
  setSelectedHpdCompany: (
    selectedEntry: at.sense.hooks.login.hpd.HPDParentComboBean
  ) => void;
  applySelectedHpdCompany: (
    selectedEntry: at.sense.hooks.login.hpd.HPDParentComboBean
  ) => void;

  authenticate: (buttonTarget: EventTarget | null) => void;
  configureSamlLoginCallback: (availableHpdCompanies: {
    [index: string]: at.sense.hooks.login.hpd.HPDParentComboBean;
  }) => void;
  showLoadMask: () => void;
  reset: () => void;
};

/**
 * A store for login credentials.
 */
export const [
  useLoginCredentialsStore,
  LoginCredentialsStoreApi
] = createStore<LoginCredentialsStore>(
  'loginCredentials.store',
  (set, get) => ({
    viewState: 'INITIAL' as VIEW_STATE,
    pageLoading: false,
    authenticated: false,
    userName: '',
    password: '',
    fingerprint: undefined,
    assertionId: undefined,
    qrCode: undefined,
    applyOtpValue: (
      authenticationCode: string,
      onCodeMatched: () => void,
      onCodeMismatched: (msg?: string) => void
    ) => {
      // apply the OTP to the login form so that it can continue later
      const otpInputEl = getOtpPasswordInput();
      if (!otpInputEl) {
        console.error('OTP input element not found');
        return;
      }
      otpInputEl.value = authenticationCode;

      // verify the OTP
      verifyTwoFactorPassword({
        url: PortletStoreApi.getState().loginServletUrl,
        user: get().userName,
        password: get().password,
        assertionId: get().assertionId,
        fingerprint: get().fingerprint,
        otp: authenticationCode
      })
        .then(result => {
          if (result.data) {
            onCodeMismatched(result.data.errorMessage);
          } else {
            onCodeMatched();
            eHealthLoginUtil.onOtpVerificationSuccess();
            const possibleNewState = determineViewStateAfterOtpVerified(
              get().availableHpdCompanies
            );
            if (possibleNewState) {
              set({
                viewState: possibleNewState
              });
            } else {
              // no next step  --> show page loading until login finished
              set({
                pageLoading: true
              });
            }
          }
        })
        .catch(e => {
          showError(e);
        });
    },
    availableTwoFactorMethods: [],
    selectedTwoFactorMethod: undefined,
    setSelectedTwoFactorMethod: (selectedEntry: OtpDetails) => {
      set({
        selectedTwoFactorMethod: selectedEntry
      });
    },
    applySelectedTwoFactorMethod: (selectedEntry: OtpDetails) => {
      eHealthLoginUtil.saveTwoFactorSelection(selectedEntry.sendMethod);

      sendTwoFactorPassword({
        url: PortletStoreApi.getState().loginServletUrl,
        user: get().userName,
        password: get().password,
        assertionId: get().assertionId,
        twofa: selectedEntry.sendMethod
      })
        .then(p => {
          const otpDetails = getUniqueOtpDetails(p.data);
          if (otpDetails) {
            set({
              otpSendDetails: otpDetails,
              qrCode: p.data.qrCode,
              appSecretKey: p.data.appConfigkey,
              otpAuthUri: p.data.otpAuthUri,
              viewState: 'TWO_FACTOR_ENTERING'
            });
          }
          eHealthLoginUtil.responseHandlerOtpSendTwoFactorPasswordSuccess({
            success: true,
            dataLength: 1,
            data: [p.data]
          });
        })
        .catch(e => {
          showError(e);
        });
    },
    availableHpdCompanies: [],
    selectedHpdCompany: undefined,
    setSelectedHpdCompany: (
      selectedEntry: at.sense.hooks.login.hpd.HPDParentComboBean
    ) => {
      set({
        selectedHpdCompany: selectedEntry
      });
    },
    applySelectedHpdCompany: (
      selectedHpdEntry: at.sense.hooks.login.hpd.HPDParentComboBean
    ) => {
      // no next step  --> show page loading until login finished
      set({
        pageLoading: true
      });
      saveHpdSelection(selectedHpdEntry);
      const hpdSelectEl = getHpdSelect();
      eHealthLoginUtil.onNextStepClicked(hpdSelectEl || null);
    },
    authenticate: (target: EventTarget | null): void => {
      const loginRequestDetails = eHealthLoginUtil.beforeAuthenticateUserCall(
        target
      );
      if (!loginRequestDetails) {
        return;
      }
      set({
        pageLoading: true
      });
      doAuthenticateUserCall({
        url: PortletStoreApi.getState().loginServletUrl,
        user: loginRequestDetails.userName,
        password: loginRequestDetails.password,
        fingerprint: loginRequestDetails.deviceFingerprint
      })
        .then(response => {
          PortletStoreApi.getState().incrementNrOfOtpAttempts();
          eHealthLoginUtil.responseHandlerAuthenticateUserSuccess(
            {
              data: [response.data],
              dataLength: 1,
              success: response.success
            },
            loginRequestDetails.clickedItem
          );
          const companyOIDToName = response.data.companyOIDToName;
          const availableHpdCompanies = companyOIDToName
            ? Object.values(companyOIDToName)
            : [];

          const availableTwoFactorMethods = getAllAvailableOtpDetails(
            response.data
          );
          const state =
            determineViewStateAfterAuthentication(
              availableTwoFactorMethods,
              availableHpdCompanies
            ) || get().viewState;
          set(
            {
              authenticated: true,
              userName: loginRequestDetails.userName,
              password: loginRequestDetails.password,
              fingerprint: loginRequestDetails.deviceFingerprint,
              assertionId: loginRequestDetails.uniqueAssertionLookupID,
              availableTwoFactorMethods: availableTwoFactorMethods,
              selectedTwoFactorMethod:
                availableTwoFactorMethods.length > 0
                  ? availableTwoFactorMethods[0]
                  : undefined,
              availableHpdCompanies: availableHpdCompanies,
              selectedHpdCompany:
                availableHpdCompanies.length > 0
                  ? availableHpdCompanies[0]
                  : undefined,
              otpSendDetails: getUniqueOtpDetails(response.data),
              viewState: state,
              pageLoading: false
            },
            'authenticateUser'
          );
        })
        .catch(e => {
          LoginCredentialsStoreApi.getState().reset();
          eHealthLoginUtil.responseHandlerAuthenticateUserFailure(e);
        });
    },
    configureSamlLoginCallback: (hpdConfig: {
      [index: string]: at.sense.hooks.login.hpd.HPDParentComboBean;
    }) => {
      const availableHpdCompanies = hpdConfig ? Object.values(hpdConfig) : [];
      const selectionNeeded = availableHpdCompanies.length >= 2;
      set({
        availableHpdCompanies: availableHpdCompanies,
        selectedHpdCompany:
          availableHpdCompanies.length > 0
            ? availableHpdCompanies[0]
            : undefined,
        viewState: selectionNeeded ? 'HPD_SELECTION' : 'INITIAL',
        pageLoading: !selectionNeeded
      });
    },
    showLoadMask: () => {
      set({
        pageLoading: true
      });
    },
    reset: () =>
      set({
        authenticated: false,
        userName: '',
        password: '',
        fingerprint: undefined,
        assertionId: undefined,
        otpSendDetails: undefined,
        availableTwoFactorMethods: [],
        selectedTwoFactorMethod: undefined,
        availableHpdCompanies: [],
        selectedHpdCompany: undefined
      })
  })
);

function determineViewStateAfterAuthentication(
  availableTwoFactorMethods: OtpDetails[],
  availableHpdCompanies: at.sense.hooks.login.hpd.HPDParentComboBean[]
): VIEW_STATE | undefined {
  if (availableTwoFactorMethods.length >= 2) {
    return 'TWO_FACTOR_METHOD_SELECTION';
  } else if (availableTwoFactorMethods.length === 1) {
    return 'TWO_FACTOR_ENTERING';
  }
  if (availableHpdCompanies.length > 1) {
    return 'HPD_SELECTION';
  }
  return undefined;
}

function determineViewStateAfterOtpVerified(
  availableHpdCompanies: at.sense.hooks.login.hpd.HPDParentComboBean[]
): VIEW_STATE | undefined {
  if (availableHpdCompanies.length > 1) {
    return 'HPD_SELECTION';
  }
  return undefined;
}
